import { Link } from "react-router-dom";
import type { MediaItem, NewsCategory, NewsItem } from "@/types/content";

type NewsSectionProps = {
  news: NewsItem[];
  ranking: MediaItem[];
};

const categoryTone: Record<NewsCategory, string> = {
  资讯: "bg-primary-50 text-primary-700 border-primary-100",
  影评: "bg-accent-50 text-accent-800 border-accent-100",
  幕后: "bg-secondary-50 text-secondary-800 border-secondary-100",
};

const splitDate = (date: string) => {
  const [year, month, day] = date.split("-");
  return { year, month, day };
};

export default function NewsSection({ news, ranking }: NewsSectionProps) {
  return (
    <section className="shell py-14 md:py-16">
      <div className="grid gap-10 lg:grid-cols-[1.65fr_1fr] lg:gap-12">
        <div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs tracking-[0.2em] text-accent-600">
                <span className="h-px w-6 bg-current"></span>
                影视资讯
              </p>
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                最新影视动态
              </h2>
            </div>
            <Link
              to="/category/news"
              className="group inline-flex shrink-0 cursor-pointer items-center gap-1 text-sm text-primary-600 transition hover:text-accent-600"
            >
              全部资讯
              <i className="ri-arrow-right-line transition group-hover:translate-x-1"></i>
            </Link>
          </div>

          <ul className="mt-6 divide-y divide-background-200 border-y border-background-200">
            {news.map((item) => {
              const date = splitDate(item.date);
              return (
                <li key={item.id}>
                  <Link
                    to={`/detail/${item.id}`}
                    className="group flex cursor-pointer flex-col gap-3 py-5 transition sm:flex-row sm:items-start sm:gap-5"
                  >
                    <div className="flex shrink-0 items-center gap-3 sm:w-16 sm:flex-col sm:items-start sm:gap-0">
                      <span className="font-heading text-2xl font-semibold leading-none text-primary-600 transition group-hover:text-accent-600">
                        {date.day}
                      </span>
                      <span className="text-[11px] text-foreground-500">
                        {date.year}.{date.month}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded border px-1.5 py-0.5 text-[10px] ${
                            categoryTone[item.category]
                          }`}
                        >
                          {item.category}
                        </span>
                        <h3 className="line-clamp-1 text-sm font-medium text-foreground-950 transition group-hover:text-primary-600 md:text-[15px]">
                          {item.title}
                        </h3>
                      </div>
                      <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-[13px]">
                        {item.summary}
                      </p>
                    </div>
                    <i className="ri-arrow-right-line hidden self-center text-foreground-300 transition group-hover:translate-x-1 group-hover:text-accent-600 sm:block"></i>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[11px] tracking-[0.2em] text-foreground-500">TIANMA CHART</p>
              <h2 className="mt-1 font-heading text-lg font-semibold text-foreground-950 md:text-xl">
                本周热度榜
              </h2>
            </div>
            <span className="rounded-full border border-accent-200 bg-accent-50 px-2 py-0.5 text-[10px] text-accent-800">
              实时
            </span>
          </div>

          <ol className="mt-5 space-y-1">
            {ranking.map((item, index) => (
              <li key={item.id}>
                <Link
                  to={`/detail/${item.id}`}
                  className="group flex cursor-pointer items-center gap-3 rounded-md px-1 py-2.5 transition hover:bg-background-50"
                >
                  <span
                    className={`w-5 shrink-0 font-heading text-sm font-semibold ${
                      index === 0 ? "text-accent-600" : "text-foreground-400"
                    }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-foreground-900 transition group-hover:text-primary-600">
                      {item.title}
                    </p>
                    <p className="mt-0.5 truncate text-[11px] text-foreground-500">
                      {item.year} · {item.genres.join(" / ")}
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 text-xs text-accent-700">
                    <i className="ri-fire-line text-xs"></i>
                    {item.hot}
                  </span>
                </Link>
              </li>
            ))}
          </ol>

          <Link
            to="/category/movie"
            className="mt-4 flex cursor-pointer items-center justify-center gap-1 rounded-md border border-background-300 bg-background-50 py-2.5 text-xs text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
          >
            查看完整榜单
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}